import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('GooglePlacesService')

const GOOGLE_API_KEY = process.env.GOOGLE_PLACES_API_KEY || process.env.GOOGLE_MAPS_API_KEY || process.env.VITE_GOOGLE_MAPS_API_KEY

// Batch verification queue for optimization
interface BatchQueueItem {
  query: string
  target: { lat: number; lng: number }
  options?: any
  resolve: (result: PlaceVerificationResult) => void
  reject: (error: Error) => void
}

const verificationQueue: BatchQueueItem[] = []
let batchTimer: NodeJS.Timeout | null = null
const BATCH_DELAY_MS = 100 // Wait 100ms to collect batch
const BATCH_SIZE = 10 // Process up to 10 verifications at once

export interface PlaceVerificationResult {
  matched: boolean
  confidence: number
  distanceMeters?: number
  name?: string
  location?: { coordinates: { lat: number; lng: number } }
  source: string
  placeId?: string
  rating?: number
  types?: string[]
  photoReference?: string // Photo reference for image fetching
  // Multi-source metadata (optional, added during parallel verification)
  multiSource?: {
    google?: {
      matched: boolean
      confidence: number
      placeId?: string
    }
    mapbox?: {
      matched: boolean
      confidence: number
    }
    agreement?: 'both' | 'partial' | 'none'
  }
}

/**
 * Google Places API Service
 * 
 * Free tier: 25,000 requests/month for Text Search + Place Details
 * Used for high-confidence POI verification
 */
export class GooglePlacesService {
  private readonly baseUrl = 'https://maps.googleapis.com/maps/api/place'

  /**
   * Verify a place exists near target coordinates using Google Places API
   * Uses batching for optimization (waits 100ms to collect multiple requests)
   * 
   * @param query - Place name or search query
   * @param target - Expected coordinates {lat, lng}
   * @param options - Search options
   * @returns Verification result with confidence score
   */
  async verifyPlace(
    query: string,
    target: { lat: number; lng: number },
    options?: {
      proximity?: { lat: number; lng: number }
      maxDistanceMeters?: number
      language?: string
      skipBatch?: boolean // Force immediate execution
    }
  ): Promise<PlaceVerificationResult> {
    if (!GOOGLE_API_KEY) {
      logger.warn('Google Places API key not configured, skipping verification')
      return { matched: false, confidence: 0, source: 'google-places' }
    }

    // If batching is disabled or skipBatch is true, execute immediately
    if (options?.skipBatch) {
      return this.verifyPlaceImmediate(query, target, options)
    }

    // Add to batch queue and wait for batch processing
    return new Promise<PlaceVerificationResult>((resolve, reject) => {
      verificationQueue.push({ query, target, options, resolve, reject })

      // Debounce: wait 100ms before processing batch
      if (batchTimer) {
        clearTimeout(batchTimer)
      }
      batchTimer = setTimeout(() => {
        void this.processBatch()
      }, BATCH_DELAY_MS)
    })
  }

  /**
   * Process batched verification requests
   * Reduces API calls by grouping nearby verifications
   */
  private async processBatch() {
    if (verificationQueue.length === 0) return

    const batch = verificationQueue.splice(0, BATCH_SIZE)
    logger.debug(`Processing batch of ${batch.length} verifications`)

    // Process all verifications in parallel (but as a single "batch")
    await Promise.all(
      batch.map(async (item) => {
        try {
          const result = await this.verifyPlaceImmediate(item.query, item.target, item.options)
          item.resolve(result)
        } catch (error) {
          item.reject(error instanceof Error ? error : new Error(String(error)))
        }
      })
    )
  }

  /**
   * Immediate verification without batching
   */
  private async verifyPlaceImmediate(
    query: string,
    target: { lat: number; lng: number },
    options?: {
      proximity?: { lat: number; lng: number }
      maxDistanceMeters?: number
      language?: string
    }
  ): Promise<PlaceVerificationResult> {
    try {
      // Use Text Search API (findplacefromtext is more precise than textsearch)
      const searchUrl = new URL(`${this.baseUrl}/findplacefromtext/json`)
      const proximity = options?.proximity ?? target
      
      searchUrl.searchParams.set('input', query)
      searchUrl.searchParams.set('inputtype', 'textquery')
      searchUrl.searchParams.set('fields', 'place_id,name,geometry,rating,types,photos')
      searchUrl.searchParams.set('locationbias', `circle:${options?.maxDistanceMeters || 5000}@${proximity.lat},${proximity.lng}`)
      if (GOOGLE_API_KEY) {
        searchUrl.searchParams.set('key', GOOGLE_API_KEY)
      }
      
      if (options?.language) {
        searchUrl.searchParams.set('language', options.language)
      }

      const response = await fetch(searchUrl.toString())
      
      if (!response.ok) {
        throw new Error(`Google Places API error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json() as {
        status: string
        candidates?: Array<{
          place_id: string
          name: string
          geometry: { location: { lat: number; lng: number } }
          rating?: number
          types?: string[]
          photos?: Array<{ photo_reference: string; height: number; width: number }>
        }>
        error_message?: string
      }

      // Check API status
      if (data.status === 'ZERO_RESULTS' || !data.candidates?.length) {
        logger.debug('No Google Places results found', { query, target })
        return { matched: false, confidence: 0, source: 'google-places' }
      }

      if (data.status !== 'OK') {
        logger.warn('Google Places API returned non-OK status', { 
          status: data.status, 
          error: data.error_message 
        })
        return { matched: false, confidence: 0, source: 'google-places' }
      }

      // Get first candidate (most relevant)
      const candidate = data.candidates[0]
      const location = candidate.geometry.location
      
      // Calculate distance from target
      const distanceMeters = calculateDistance(target, location)
      const maxDistanceMeters = options?.maxDistanceMeters ?? 25_000
      const withinRadius = distanceMeters <= maxDistanceMeters

      // Calculate confidence score
      // - Distance score: closer = higher confidence
      // - Name match: exact match gets bonus (checked by Google's relevance)
      // - Rating: places with ratings are more established/real
      const distanceScore = Math.max(0, 100 - (distanceMeters / maxDistanceMeters) * 100)
      const ratingBonus = candidate.rating ? Math.min(20, candidate.rating * 4) : 0
      const googleRelevanceBonus = 10 // Google's algorithm already filtered by relevance
      
      const confidence = Math.min(100, Math.round(
        distanceScore * 0.7 + 
        ratingBonus + 
        googleRelevanceBonus
      ))

      logger.debug('Google Places verification result', {
        query,
        matched: withinRadius,
        confidence,
        distanceMeters,
        placeId: candidate.place_id
      })

      return {
        matched: withinRadius,
        confidence,
        distanceMeters,
        name: candidate.name,
        location: { coordinates: location },
        source: 'google-places',
        placeId: candidate.place_id,
        rating: candidate.rating,
        types: candidate.types,
        photoReference: candidate.photos?.[0]?.photo_reference
      }
    } catch (error) {
      logger.error('Google Places verification error', { error, query, target })
      return { matched: false, confidence: 0, source: 'google-places' }
    }
  }

  /**
   * Search for nearby places by category
   * Used as fallback when AI generation fails
   */
  async searchNearby(
    location: { lat: number; lng: number },
    keyword: string,
    radiusMeters: number,
    options?: {
      type?: string
      language?: string
    }
  ): Promise<Array<{
    name: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    description: string
    rating?: number
    placeId: string
    types: string[]
  }>> {
    if (!GOOGLE_API_KEY) {
      logger.warn('Google Places API key not configured')
      return []
    }

    try {
      const url = new URL(`${this.baseUrl}/nearbysearch/json`)
      
      url.searchParams.set('location', `${location.lat},${location.lng}`)
      url.searchParams.set('radius', Math.min(radiusMeters, 50000).toString()) // Max 50km
      url.searchParams.set('keyword', keyword)
      url.searchParams.set('key', GOOGLE_API_KEY)
      
      if (options?.type) {
        url.searchParams.set('type', options.type)
      }
      
      if (options?.language) {
        url.searchParams.set('language', options.language)
      }

      const response = await fetch(url.toString())
      
      if (!response.ok) {
        throw new Error(`Google Places API error: ${response.status}`)
      }

      const data = await response.json() as {
        status: string
        results?: Array<{
          place_id: string
          name: string
          geometry: { location: { lat: number; lng: number } }
          rating?: number
          types: string[]
          vicinity?: string
        }>
      }

      if (data.status !== 'OK' || !data.results) {
        return []
      }

      return data.results.map(place => ({
        name: place.name,
        location: { coordinates: place.geometry.location },
        category: place.types[0] || 'poi',
        description: place.vicinity || '',
        rating: place.rating,
        placeId: place.place_id,
        types: place.types
      }))
    } catch (error) {
      logger.error('Google Places search error', { error, location, keyword })
      return []
    }
  }

  /**
   * Get photo URL from Google Places photo reference
   * 
   * @param photoReference - Photo reference from Place Details
   * @param maxWidth - Maximum width in pixels (default 400)
   * @returns Full URL to photo image
   */
  getPlacePhotoUrl(photoReference: string, maxWidth: number = 400): string {
    if (!GOOGLE_API_KEY) {
      return ''
    }
    
    return `${this.baseUrl}/photo?maxwidth=${maxWidth}&photo_reference=${photoReference}&key=${GOOGLE_API_KEY}`
  }
}
